/**
 * Validation des destinations de redirection après authentification.
 *
 * SÉCURITÉ (audit 2026-09-10) — redirection ouverte.
 *
 * Le paramètre `returnTo` était consommé sans aucun contrôle à trois endroits :
 *   - src/app/api/auth/google/route.ts        → stocké tel quel dans le cookie
 *                                                `oauth_return`
 *   - src/app/api/auth/google/callback/route.ts → `NextResponse.redirect(new
 *                                                URL(returnTo, url))`
 *   - src/components/auth/LoginForm.tsx et AuthFlowClient.tsx → `router.push()`
 *
 * `new URL("https://exemple-malveillant.test", base)` renvoie l'URL absolue :
 * la base est ignorée. Un lien
 * `https://sahha.ma/api/auth/google?returnTo=https://…` menait donc la victime
 * à se connecter réellement chez nous, cookie de session posé, puis à atterrir
 * sur le site de l'attaquant — hameçonnage crédible, et fuite du Referer.
 *
 * Ce module est volontairement SANS `server-only` : il est utilisé côté serveur
 * (routes API) comme côté client (formulaires de connexion), et la règle doit
 * être exactement la même des deux côtés.
 */

/** Hôte canonique et ses sous-domaines, seuls autorisés en absolu. */
const ALLOWED_HOST_SUFFIX = ".sahha.ma";
const ALLOWED_HOST = "sahha.ma";

function isAllowedHost(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return h === ALLOWED_HOST || h.endsWith(ALLOWED_HOST_SUFFIX);
}

/**
 * Renvoie `value` si c'est une destination interne sûre, sinon `fallback`.
 *
 * Sont acceptés :
 *   - un chemin relatif à la racine : "/mon-compte", "/espace-pro/agenda?x=1#a"
 *   - une URL absolue https sur sahha.ma ou un de ses sous-domaines — cas
 *     légitime : la page /connexion redirige vers pro.sahha.ma en conservant
 *     le returnTo (cf. src/app/[locale]/connexion/page.tsx).
 *
 * Sont refusés :
 *   - tout autre hôte, y compris "sahha.ma.exemple.test" (d'où la comparaison
 *     sur le suffixe AVEC le point, jamais un simple `includes`)
 *   - "//evil.test" et "/\\evil.test" : URL dites protocol-relative, que le
 *     navigateur résout en changement de domaine
 *   - "http://" en absolu (rétrogradation de protocole)
 *   - les schémas non http : "javascript:", "data:", "vbscript:"
 *   - les caractères de contrôle (dont \n, \r, \t) utilisés pour contourner
 *     les filtres naïfs et injecter des en-têtes
 */
export function safeReturnTo(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;

  const raw = value.trim();
  if (!raw) return fallback;

  // Caractères de contrôle → rejet immédiat, avant toute autre analyse.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(raw)) return fallback;

  // Chemin relatif — le cas courant.
  if (raw.startsWith("/")) {
    // "//host" et "/\host" sont interprétés comme protocol-relative par les
    // navigateurs : ce sont des redirections externes déguisées.
    if (raw.startsWith("//") || raw.startsWith("/\\")) return fallback;
    return raw;
  }

  // URL absolue — uniquement https sur la famille sahha.ma.
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== "https:") return fallback;
    if (!isAllowedHost(parsed.hostname)) return fallback;
    return parsed.toString();
  } catch {
    // Ni chemin absolu, ni URL analysable (ex. "mon-compte", "javascript:…").
    return fallback;
  }
}
